/**
 * Cascadia tile rendering — matches the signed-off wireframe design
 * (docs/wireframes/cascadia/cascadia-render.js, approved on PR #593).
 *
 * Pure SVG-fragment builders (React elements) driven by engine data:
 *   - single habitat  → flat hex fill
 *   - dual habitat     → diagonal split (top = habitat A, bottom = B),
 *                        engine convention: first habitat = edges 0-2
 *   - wildlife footprint = plain colour discs (no silhouette), laid out
 *                        1 = centred · 2 = side-by-side · 3 = reversed
 *                        triangle (10/2/6 o'clock)
 *   - keystone marker  = simple pine cone (green outer + brown inner)
 *
 * Colours come from the app's `--cn-cascadia-*` CSS vars so theme +
 * the approved palette apply. Geometry reuses svg-hex (pointy-top,
 * vertices at 60·i − 30°).
 */

import type { Habitat, TileDef, Wildlife } from "@cloudnest/cascadia-engine";
import { hexCorners } from "./svg-hex";

const HABITAT_FILL: Record<Habitat, string> = {
  forest: "var(--cn-cascadia-hab-forest)",
  mountain: "var(--cn-cascadia-hab-mountain)",
  prairie: "var(--cn-cascadia-hab-prairie)",
  river: "var(--cn-cascadia-hab-river)",
  wetland: "var(--cn-cascadia-hab-wetland)",
};

const WILDLIFE_FILL: Record<Wildlife, string> = {
  bear: "var(--cn-cascadia-wild-bear)",
  elk: "var(--cn-cascadia-wild-elk)",
  salmon: "var(--cn-cascadia-wild-salmon)",
  hawk: "var(--cn-cascadia-wild-hawk)",
  fox: "var(--cn-cascadia-wild-fox)",
};

/** Distinct habitats in engine order: [A] keystone, or [A,B] dual. */
export function habitatsOf(def: TileDef): readonly Habitat[] {
  return def.edges[0] === def.edges[3]
    ? [def.edges[0]]
    : [def.edges[0], def.edges[3]];
}

const pt = (p: { x: number; y: number }) =>
  `${p.x.toFixed(2)},${p.y.toFixed(2)}`;

/** Hex habitat fill — single colour or diagonal dual split. */
export function TileFill({
  cx,
  cy,
  size,
  habitats,
  rotation = 0,
}: {
  cx: number;
  cy: number;
  size: number;
  habitats: readonly Habitat[];
  /** Tile rotation 0..5 — visually rotates the hex by 60°·n clockwise
   * (matches the engine §583 side-index convention). */
  rotation?: number;
}) {
  const c = hexCorners(cx, cy, size);
  const outline = (
    <polygon
      points={c.map(pt).join(" ")}
      fill="none"
      stroke="var(--cn-cascadia-tile-outline)"
      strokeWidth={0.7}
    />
  );
  let inner: JSX.Element;
  if (habitats.length === 1) {
    inner = (
      <>
        <polygon
          points={c.map(pt).join(" ")}
          fill={HABITAT_FILL[habitats[0]!]}
        />
        {outline}
      </>
    );
  } else {
    // dual: split line corners[0] → corners[3]; top = A, bottom = B
    const top = [c[0]!, c[1]!, c[2]!, c[3]!].map(pt).join(" ");
    const bot = [c[3]!, c[4]!, c[5]!, c[0]!].map(pt).join(" ");
    inner = (
      <>
        <polygon points={top} fill={HABITAT_FILL[habitats[0]!]} />
        <polygon points={bot} fill={HABITAT_FILL[habitats[1]!]} />
        <line
          x1={c[0]!.x}
          y1={c[0]!.y}
          x2={c[3]!.x}
          y2={c[3]!.y}
          stroke="var(--cn-cascadia-tile-outline)"
          strokeWidth={0.5}
          opacity={0.55}
        />
        {outline}
      </>
    );
  }
  const deg = ((rotation % 6) + 6) % 6 === 0 ? 0 : (rotation % 6) * 60;
  return deg === 0 ? (
    inner
  ) : (
    <g transform={`rotate(${deg} ${cx} ${cy})`}>{inner}</g>
  );
}

/** Wildlife-option footprint: plain colour discs, 1/2/3 layout. */
export function Footprint({
  cx,
  cy,
  size,
  options,
}: {
  cx: number;
  cy: number;
  size: number;
  options: readonly Wildlife[];
}) {
  const n = options.length;
  if (n === 0) return null;
  const slot = size * 0.4;
  const rad = slot * 0.62;
  let pos: readonly [number, number][];
  if (n === 1) {
    pos = [[0, 0]];
  } else if (n === 2) {
    const dx = rad * 1.08;
    pos = [
      [-dx, 0],
      [dx, 0],
    ];
  } else {
    const dx = rad;
    pos = [
      [-dx, -rad * 0.66],
      [dx, -rad * 0.66],
      [0, rad],
    ];
  }
  const haloR =
    Math.max(...pos.map(([px, py]) => Math.hypot(px, py))) + rad + slot * 0.3;
  return (
    <g style={{ pointerEvents: "none" }}>
      <circle
        cx={cx}
        cy={cy}
        r={haloR}
        fill="var(--cn-cascadia-card-bg)"
        opacity={0.78}
      />
      {options.map((w, i) => (
        <circle
          key={`fp-${i}-${w}`}
          cx={cx + pos[i]![0]}
          cy={cy + pos[i]![1]}
          r={rad}
          fill={WILDLIFE_FILL[w]}
          stroke="var(--cn-cascadia-card-bg)"
          strokeWidth={1}
        />
      ))}
    </g>
  );
}

/** Keystone nature marker = simple pine cone (green outer + brown inner). */
export function KeystoneMarker({
  cx,
  cy,
  size,
}: {
  cx: number;
  cy: number;
  size: number;
}) {
  const my = cy - size + size * 0.16;
  const ro = Math.max(3.4, size * 0.15);
  return (
    <g style={{ pointerEvents: "none" }}>
      <circle cx={cx} cy={my} r={ro} fill="var(--cn-cascadia-hab-forest)" />
      <circle cx={cx} cy={my} r={ro * 0.5} fill="#6e4a23" />
    </g>
  );
}
